import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  makeStyles,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import imgLogo from '../assests/img/logo-login.svg';
import backgroundImg from '../assests/img/bg_login.jpeg';
import { useLoginMutation } from '../services/auth.service';
import { useSnackbar } from '../hooks/useSnackbar';
import { useLocalStorage } from '../hooks/useLocalStorage';
import useModalConfirm from '../hooks/useModalConfirm';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  textLogo: { fontWeight: 'bold', fontSize: '28px', marginBottom: '30px' },
  leftTextLogo: { color: '#000000' },
  rightTextLogo: { color: '#8F0A0C' },
  textRight: {
    padding: '32px',
    textAlign: 'center',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '16px',
    background: '#D9042F',
    position: 'absolute',
    bottom: '0',
    right: '0',
    width: '100%',
    boxSizing: 'border-box',
    textTransform: 'uppercase',
  },
  textBottomPage: {
    color: '#BDBDBD',
    textAlign: 'center',
    width: '100%',
    marginTop: '18vh',
    fontSize: '16px',
  },
  loginButton: {
    marginBottom: '16px',
    padding: '10px 20px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    fontSize: '18px',
    fontWeight: 500,
  },
  redText: {
    color: '#D9042F',
  },
  redButton: {
    backgroundColor: '#D9042F',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#b80327',
    },
    '&:disabled': {
      backgroundColor: '#D9042F80',
      color: '#fff',
    },
  },
  textField: {
    '& .MuiInputLabel-root': {
      color: '#666',
      fontSize: '16px',
    },
    '&:hover .MuiInputLabel-root': {
      color: '#D9042F',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#666',
    },
    '&:hover .MuiInput-underline:before': {
      borderBottomColor: '#D9042F',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#D9042F',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Roboto',
      fontSize: '16px',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '12px',
    },
  },
  checkbox: {
    '&.MuiCheckbox-root': {
      color: '#666',
    },
    '&.Mui-checked': {
      color: '#D9042F',
    },
  },
}));

const LoginScreen = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { showModalConfirm, hideModalConfirm } = useModalConfirm();
  const [loginUser, { isLoading }] = useLoginMutation();
  const { setSnackbar } = useSnackbar();
  const [token] = useLocalStorage('access_token', '');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || '/control';

  useEffect(() => {
    if (token) {
      navigate(from);
    }
  }, [token, navigate, from]);

  const handleForgotPass = () => {
    // showModalConfirm({
    //   title: 'Quên mật khẩu',
    //   content: 'Vui lòng liên hệ Admin để được cấp lại mật khẩu của tài khoản',
    //   confirm: {
    //     action: hideModalConfirm,
    //   },
    // });
  };

  const handleCheckRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  return (
    <div className={classes.root} title="Đăng nhập">
      <Grid container style={{ height: '100%' }}>
        <Grid item xs={12} md={6}>
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="center"
          >
            <Container maxWidth="sm" style={{ marginTop: '50px' }}>
              <Formik
                initialValues={{
                  username: '',
                  password: '',
                }}
                validationSchema={Yup.object().shape({
                  username: Yup.string()
                    .trim()
                    .min(4, 'Tên đăng nhập tối thiểu 6 kí tự')
                    .required('Tên đăng nhập không được để trống'),
                  password: Yup.string()
                    .trim()
                    .min(6, 'Mật khẩu tối thiểu 6 kí tự')
                    .required('Mật khẩu không được để trống'),
                })}
                onSubmit={async (values) => {
                  try {
                    await loginUser(values).unwrap();
                    navigate(from);
                  } catch (error) {
                    setSnackbar({
                      open: true,
                      message: 'Sai tài khoản hoặc mật khẩu',
                      severity: 'error',
                    });
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Box mb={1} style={{ textAlign: 'left' }}>
                      <div
                        style={{
                          width: '80px',
                          height: '80px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          alt="Logo"
                          src={imgLogo}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                      <Typography
                        className={classes.redText}
                        style={{
                          fontSize: '30px',
                          marginTop: '32px',
                          fontWeight: '700',
                        }}
                        variant="h2"
                      >
                        Đăng nhập
                      </Typography>
                      <br />
                    </Box>
                    <TextField
                      className={classes.textField}
                      error={Boolean(touched.username && errors.username)}
                      fullWidth
                      helperText={touched.username && errors.username}
                      label="Tên đăng nhập"
                      margin="normal"
                      name="username"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.username}
                    />
                    <TextField
                      className={classes.textField}
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      label="Mật khẩu"
                      margin="normal"
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={showPassword ? 'text' : 'password'}
                      value={values.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                            >
                              {showPassword ? (
                                <VisibilityOff style={{ color: '#D9042F' }} />
                              ) : (
                                <Visibility style={{ color: '#D9042F' }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box my={2}>
                      <Grid container>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={rememberMe}
                                classes={{
                                  root: classes.checkbox,
                                  checked: classes.checkbox,
                                }}
                                onChange={handleCheckRememberMe}
                                name="rememberMe"
                              />
                            }
                            label={
                              <span
                                style={{
                                  fontFamily: 'Roboto',
                                  fontSize: '16px',
                                }}
                              >
                                Nhớ đăng nhập
                              </span>
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#D9042F',
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              fontFamily: 'Roboto',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#D9042F',
                              fontWeight: '700',
                              fontSize: '18px',
                            }}
                            onClick={handleForgotPass}
                          >
                            Quên mật khẩu?
                          </button>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box my={2}>
                      <Button
                        className={`${classes.loginButton} ${classes.redButton}`}
                        disabled={isLoading || isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                      >
                        Đăng nhập
                        {isLoading && (
                          <div style={{ marginLeft: 20 }}>
                            <CircularProgress
                              style={{ color: '#B30326' }}
                              size={20}
                            />
                          </div>
                        )}
                      </Button>
                      <span
                        style={{
                          color: '#BDBDBD',
                          textDecoration: 'underline',
                          marginTop: '12px !important',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                        onClick={() => navigate('/privacy-policy')}
                      >
                        <a>
                          <i>Chính sách quyền riêng tư</i>
                        </a>
                      </span>
                    </Box>
                    <Box className={classes.textBottomPage}>
                      <span>
                        Sản phẩm thuộc bản quyền công ty{' '}
                        <a
                          target="_blank"
                          href="https://vntech24h.com/"
                          style={{ textDecoration: 'none' }}
                        >
                          Vntech24h.,Jsc.
                        </a>
                      </span>
                    </Box>
                  </form>
                )}
              </Formik>
            </Container>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          style={{ margin: 0, padding: 0, position: 'relative', height: '100vh' }}
        >
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="center"
          >
            <div style={{ position: 'relative', height: '100vh' }}>
              <img
                alt="Background"
                src={backgroundImg}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div className={classes.textRight}>
                <span>
                  hệ thống theo dõi camera ai vcam
                </span>
              </div>
            </div>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

export default LoginScreen;